import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  LocalPharmacy as MedicationIcon,
  Warning as WarningIcon,
  ShoppingCart as PurchaseIcon,
  RemoveCircle as UsageIcon,
  AttachMoney as MoneyIcon,
  Receipt as TransactionsIcon,
  Inventory as InventoryIcon,
  EventBusy as ExpiryIcon,
  FilterAltOff as ClearIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { medicationService, birdProcurementsService } from '../services/apiServices';
import { Medication, MedicationStatistics, MedicationTransaction } from '../types';
import MedicationFormDialog from '../components/MedicationFormDialog';
import MedicationTransactionDialog from '../components/MedicationTransactionDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { format } from 'date-fns';

// ── helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  `GH₵ ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── StatCard ──────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  color,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  color: string;
  icon: React.ReactNode;
}) {
  return (
    <Paper
      sx={{
        p: 2,
        flex: '1 1 140px',
        minWidth: 130,
        borderLeft: `4px solid ${color}`,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: `inset 0 1px 0 ${color}22`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          right: -10,
          top: -10,
          fontSize: 68,
          opacity: 0.05,
          color,
          lineHeight: 1,
          pointerEvents: 'none',
          display: 'flex',
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          fontSize: '0.62rem',
          display: 'block',
        }}
      >
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={800} sx={{ color, mt: 0.5, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

// ── type badge helpers ────────────────────────────────────────────────────────

const TX_TYPE_COLOR: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
  purchase: 'success',
  usage: 'info',
  adjustment: 'warning',
  disposal: 'error',
};

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  active: 'success',
  low_stock: 'warning',
  expired: 'error',
  discontinued: 'default',
};

// ── Page ──────────────────────────────────────────────────────────────────────

const Medications: React.FC = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'inventory' | 'transactions'>('inventory');

  // ── inventory state ──────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [transactionType, setTransactionType] = useState<
    'purchase' | 'usage' | 'adjustment' | 'disposal'
  >('purchase');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState<Medication | null>(null);
  const [procurementPickerOpen, setProcurementPickerOpen] = useState(false);
  const [selectedProcurementId, setSelectedProcurementId] = useState<number | ''>('');

  // ── transaction state ────────────────────────────────────────────────────
  const [txPage, setTxPage] = useState(1);
  const [txPageSize, setTxPageSize] = useState(15);
  const [txTypeFilter, setTxTypeFilter] = useState('all');
  const [txMedFilter, setTxMedFilter] = useState('all');
  const [txDateFrom, setTxDateFrom] = useState('');
  const [txDateTo, setTxDateTo] = useState('');
  const [txDeleteTarget, setTxDeleteTarget] = useState<MedicationTransaction | null>(null);

  // ── debounce search ──────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ── queries ──────────────────────────────────────────────────────────────
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      'medications',
      {
        search: debouncedSearch || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        type: typeFilter === 'all' ? undefined : typeFilter,
        page,
        per_page: pageSize,
      },
    ],
    queryFn: async () => {
      const f: any = { page, per_page: pageSize };
      if (debouncedSearch) f.search = debouncedSearch;
      if (statusFilter !== 'all') f.status = statusFilter;
      if (typeFilter !== 'all') f.type = typeFilter;
      return medicationService.getAll(f);
    },
  });

  const { data: statistics } = useQuery<MedicationStatistics>({
    queryKey: ['medication-statistics'],
    queryFn: medicationService.getStatistics,
  });

  const { data: medicationTypes } = useQuery<string[]>({
    queryKey: ['medication-types'],
    queryFn: medicationService.getTypes,
  });

  const {
    data: txData,
    isFetching: txFetching,
    isLoading: txLoading,
  } = useQuery({
    queryKey: [
      'medication-transactions',
      {
        transaction_type: txTypeFilter === 'all' ? undefined : txTypeFilter,
        medication_id: txMedFilter === 'all' ? undefined : txMedFilter,
        date_from: txDateFrom || undefined,
        date_to: txDateTo || undefined,
        page: txPage,
        per_page: txPageSize,
      },
    ],
    queryFn: async () => {
      const f: any = { page: txPage, per_page: txPageSize };
      if (txTypeFilter !== 'all') f.transaction_type = txTypeFilter;
      if (txMedFilter !== 'all') f.medication_id = parseInt(txMedFilter);
      if (txDateFrom) f.date_from = txDateFrom;
      if (txDateTo) f.date_to = txDateTo;
      return medicationService.getAllTransactions(f);
    },
    enabled: tab === 'transactions',
  });

  // Full medication list for the transaction filter dropdown (no pagination)
  const { data: allMedicationsData } = useQuery({
    queryKey: ['medications-all-names'],
    queryFn: () => medicationService.getAll({ per_page: 200 }),
    staleTime: 60_000,
  });
  const allMedications: Medication[] = allMedicationsData?.data ?? [];

  const { data: procurementsData } = useQuery({
    queryKey: ['bird-procurements-list'],
    queryFn: () => birdProcurementsService.getAll({ per_page: 100 }),
    enabled: procurementPickerOpen,
  });
  const procurements = procurementsData?.data ?? [];

  // ── mutations ────────────────────────────────────────────────────────────
  const deleteMedMutation = useMutation({
    mutationFn: (id: number) => medicationService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medications'] });
      queryClient.invalidateQueries({ queryKey: ['medication-statistics'] });
      setDeleteDialogOpen(false);
      setSelectedMedication(null);
    },
  });

  const deleteTxMutation = useMutation({
    mutationFn: (id: number) => medicationService.deleteTransaction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medication-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['medications'] });
      queryClient.invalidateQueries({ queryKey: ['medication-statistics'] });
      setTxDeleteTarget(null);
    },
  });

  // ── handlers ─────────────────────────────────────────────────────────────
  const handleTransaction = (
    med: Medication,
    type: 'purchase' | 'usage' | 'adjustment' | 'disposal'
  ) => {
    setSelectedMedication(med);
    setTransactionType(type);
    if (type === 'usage') {
      setSelectedProcurementId('');
      setProcurementPickerOpen(true);
    } else {
      setTransactionDialogOpen(true);
    }
  };

  // ── inventory columns ────────────────────────────────────────────────────
  const invColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Medication',
      flex: 1.5,
      minWidth: 180,
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.9,
      minWidth: 110,
      renderCell: (p: GridRenderCellParams) => (
        <Chip label={p.value} size="small" color="primary" variant="outlined" />
      ),
    },
    {
      field: 'manufacturer',
      headerName: 'Manufacturer',
      flex: 1,
      minWidth: 140,
      renderCell: (p: GridRenderCellParams) => p.value || '—',
    },
    {
      field: 'quantity_in_stock',
      headerName: 'Stock',
      flex: 0.8,
      minWidth: 100,
      renderCell: (p: GridRenderCellParams) => {
        const med = p.row as Medication;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              variant="body2"
              fontWeight={med.is_low_stock ? 700 : 500}
              color={med.is_low_stock ? 'warning.main' : 'inherit'}
            >
              {p.value} {med.unit}
            </Typography>
            {med.is_low_stock && (
              <Tooltip title="Low stock">
                <WarningIcon sx={{ fontSize: 14, color: '#ff9800' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'unit_cost',
      headerName: 'Unit Cost',
      flex: 0.8,
      minWidth: 100,
      renderCell: (p: GridRenderCellParams) => fmt(p.value as number),
    },
    {
      field: 'expiry_date',
      headerName: 'Expiry',
      flex: 1,
      minWidth: 130,
      renderCell: (p: GridRenderCellParams) => {
        if (!p.value)
          return (
            <Typography variant="caption" color="text.secondary">
              No expiry
            </Typography>
          );
        const med = p.row as Medication;
        const soon =
          med.days_until_expiry != null && med.days_until_expiry <= 30 && med.days_until_expiry > 0;
        return (
          <Stack spacing={0.5} sx={{ py: 0.75 }}>
            <Typography
              variant="body2"
              fontWeight={med.is_expired || soon ? 600 : 400}
              color={med.is_expired ? 'error.main' : soon ? 'warning.main' : 'inherit'}
            >
              {format(new Date(p.value as string), 'dd MMM yyyy')}
            </Typography>
            {med.is_expired && (
              <Chip
                label="Expired"
                size="small"
                color="error"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
            {soon && (
              <Chip
                label={`${med.days_until_expiry}d left`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
          </Stack>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 100,
      renderCell: (p: GridRenderCellParams) => (
        <Chip
          label={(p.value as string)?.replace('_', ' ').toUpperCase()}
          size="small"
          color={STATUS_COLOR[p.value as string] ?? 'default'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      sortable: false,
      renderCell: (p: GridRenderCellParams) => {
        const med = p.row as Medication;
        return (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ height: '100%' }}>
            <Tooltip title="Purchase">
              <IconButton
                size="small"
                sx={{ color: '#66bb6a' }}
                onClick={() => handleTransaction(med, 'purchase')}
              >
                <PurchaseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Record Usage">
              <IconButton
                size="small"
                sx={{ color: '#42a5f5' }}
                onClick={() => handleTransaction(med, 'usage')}
              >
                <UsageIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                sx={{ color: '#ff9800' }}
                onClick={() => {
                  setSelectedMedication(med);
                  setFormDialogOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                sx={{ color: 'rgba(244,67,54,0.7)', '&:hover': { color: '#f44336' } }}
                onClick={() => {
                  setSelectedMedication(med);
                  setDeleteDialogOpen(true);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  // ── transaction columns ──────────────────────────────────────────────────
  const txColumns: GridColDef[] = [
    {
      field: 'transaction_date',
      headerName: 'Date',
      flex: 0.9,
      minWidth: 110,
      renderCell: (p: GridRenderCellParams) => format(new Date(p.value as string), 'dd MMM yyyy'),
    },
    {
      field: 'medication',
      headerName: 'Medication',
      flex: 1.4,
      minWidth: 170,
      renderCell: (p: GridRenderCellParams) => p.value?.name || '—',
    },
    {
      field: 'transaction_type',
      headerName: 'Type',
      flex: 0.9,
      minWidth: 110,
      renderCell: (p: GridRenderCellParams) => (
        <Chip
          label={(p.value as string)?.toUpperCase()}
          size="small"
          color={TX_TYPE_COLOR[p.value as string] ?? 'default'}
        />
      ),
    },
    {
      field: 'quantity',
      headerName: 'Qty',
      flex: 0.75,
      minWidth: 90,
      renderCell: (p: GridRenderCellParams) => {
        const isIn =
          p.row.transaction_type === 'purchase' || p.row.transaction_type === 'adjustment';
        return (
          <Typography variant="body2" color={isIn ? 'success.main' : 'error.main'} fontWeight={600}>
            {isIn ? '+' : '-'}
            {p.value} {p.row.medication?.unit}
          </Typography>
        );
      },
    },
    {
      field: 'unit_cost',
      headerName: 'Unit Cost',
      flex: 0.8,
      minWidth: 100,
      renderCell: (p: GridRenderCellParams) => (p.value ? fmt(p.value as number) : '—'),
    },
    {
      field: 'total_cost',
      headerName: 'Total Cost',
      flex: 0.8,
      minWidth: 100,
      renderCell: (p: GridRenderCellParams) => (p.value ? fmt(p.value as number) : '—'),
    },
    {
      field: 'reason',
      headerName: 'Reason',
      flex: 1.2,
      minWidth: 140,
      renderCell: (p: GridRenderCellParams) => (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: 'normal', lineHeight: 1.4 }}
        >
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'user',
      headerName: 'Recorded By',
      flex: 0.9,
      minWidth: 120,
      renderCell: (p: GridRenderCellParams) => p.value?.name || '—',
    },
    {
      field: 'actions',
      headerName: '',
      width: 56,
      sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Tooltip title="Delete transaction">
          <IconButton
            size="small"
            sx={{ color: 'rgba(244,67,54,0.6)', '&:hover': { color: '#f44336' } }}
            onClick={() => setTxDeleteTarget(p.row as MedicationTransaction)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const GRID_SX = {
    '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(255,152,0,0.04)' },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 700,
      fontSize: '0.7rem',
      letterSpacing: '0.8px',
      color: 'rgba(255,152,0,0.85)',
      textTransform: 'uppercase' as const,
    },
    '& .MuiDataGrid-cell': { py: 0.75 },
  };

  return (
    <Box sx={{ p: 3 }}>
      {(isFetching || isLoading || txFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #26c6da, #ff9800)' },
          }}
        />
      )}

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #26c6da 0%, #00838f 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(38,198,218,0.4)',
            flexShrink: 0,
          }}
        >
          <MedicationIcon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
        <Box flex={1}>
          <Typography variant="h5" fontWeight={700}>
            Medications
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Inventory, stock levels, purchases, usage and expiry tracking
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedMedication(null);
            setFormDialogOpen(true);
          }}
          sx={{
            background: 'linear-gradient(135deg, #26c6da 0%, #00838f 100%)',
            color: '#000',
            flexShrink: 0,
          }}
        >
          Add Medication
        </Button>
      </Box>

      {/* ── Stats strip ── */}
      {statistics && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <StatCard
            label="Total Medications"
            value={statistics.total_medications}
            sub={`${statistics.active_medications} active`}
            color="#26c6da"
            icon={<MedicationIcon sx={{ fontSize: 'inherit' }} />}
          />
          <StatCard
            label="Low Stock"
            value={statistics.low_stock_count}
            sub="Needs restocking"
            color={statistics.low_stock_count > 0 ? '#ff9800' : '#66bb6a'}
            icon={<WarningIcon sx={{ fontSize: 'inherit' }} />}
          />
          <StatCard
            label="Expiring Soon"
            value={statistics.expiring_soon_count}
            sub="Within 30 days"
            color={statistics.expiring_soon_count > 0 ? '#ffa726' : '#66bb6a'}
            icon={<ExpiryIcon sx={{ fontSize: 'inherit' }} />}
          />
          <StatCard
            label="Expired"
            value={statistics.expired_count}
            sub="Past expiry date"
            color={statistics.expired_count > 0 ? '#f44336' : '#66bb6a'}
            icon={<ExpiryIcon sx={{ fontSize: 'inherit' }} />}
          />
          <StatCard
            label="Inventory Value"
            value={
              statistics.total_inventory_value >= 1000
                ? `GH₵ ${(statistics.total_inventory_value / 1000).toFixed(1)}k`
                : fmt(statistics.total_inventory_value)
            }
            sub={
              statistics.total_inventory_value >= 1000
                ? fmt(statistics.total_inventory_value)
                : undefined
            }
            color="#66bb6a"
            icon={<MoneyIcon sx={{ fontSize: 'inherit' }} />}
          />
        </Box>
      )}

      {/* ── Main Panel ── */}
      <Paper sx={{ border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: '1px solid rgba(255,152,0,0.15)', px: 1 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.45)',
                fontWeight: 600,
                fontSize: '0.85rem',
                minHeight: 48,
                gap: 0.75,
                '&.Mui-selected': { color: '#ff9800' },
              },
              '& .MuiTabs-indicator': {
                bgcolor: '#ff9800',
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            <Tab
              value="inventory"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InventoryIcon sx={{ fontSize: 16 }} />
                  Inventory
                  {data?.meta?.total != null && (
                    <Chip
                      label={data.meta.total}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        bgcolor:
                          tab === 'inventory' ? 'rgba(255,152,0,0.15)' : 'rgba(255,255,255,0.07)',
                        color: tab === 'inventory' ? '#ff9800' : 'text.secondary',
                      }}
                    />
                  )}
                </Box>
              }
            />
            <Tab
              value="transactions"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TransactionsIcon sx={{ fontSize: 16 }} />
                  Transactions
                  {txData?.meta?.total != null && (
                    <Chip
                      label={txData.meta.total}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        bgcolor:
                          tab === 'transactions'
                            ? 'rgba(255,152,0,0.15)'
                            : 'rgba(255,255,255,0.07)',
                        color: tab === 'transactions' ? '#ff9800' : 'text.secondary',
                      }}
                    />
                  )}
                </Box>
              }
            />
          </Tabs>
        </Box>

        <Box sx={{ p: 2 }}>
          {/* Inventory filters */}
          {tab === 'inventory' && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search medications…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ flex: '1 1 200px' }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <FormControl size="small" sx={{ flex: '0 0 140px' }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {medicationTypes?.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: '0 0 150px' }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="low_stock">Low Stock</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                  <MenuItem value="discontinued">Discontinued</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Transaction filters */}
          {tab === 'transactions' && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ flex: '1 1 160px' }}>
                <InputLabel>Medication</InputLabel>
                <Select
                  value={txMedFilter}
                  label="Medication"
                  onChange={(e) => {
                    setTxMedFilter(e.target.value);
                    setTxPage(1);
                  }}
                >
                  <MenuItem value="all">All Medications</MenuItem>
                  {allMedications.map((med) => (
                    <MenuItem key={med.id} value={med.id.toString()}>
                      {med.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: '0 0 150px' }}>
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  value={txTypeFilter}
                  label="Transaction Type"
                  onChange={(e) => {
                    setTxTypeFilter(e.target.value);
                    setTxPage(1);
                  }}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="purchase">Purchase</MenuItem>
                  <MenuItem value="usage">Usage</MenuItem>
                  <MenuItem value="adjustment">Adjustment</MenuItem>
                  <MenuItem value="disposal">Disposal</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="date"
                label="From"
                value={txDateFrom}
                onChange={(e) => {
                  setTxDateFrom(e.target.value);
                  setTxPage(1);
                }}
                sx={{ flex: '0 0 140px' }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={txDateTo}
                onChange={(e) => {
                  setTxDateTo(e.target.value);
                  setTxPage(1);
                }}
                sx={{ flex: '0 0 140px' }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              {(txMedFilter !== 'all' || txTypeFilter !== 'all' || txDateFrom || txDateTo) && (
                <Tooltip title="Clear filters">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setTxMedFilter('all');
                      setTxTypeFilter('all');
                      setTxDateFrom('');
                      setTxDateTo('');
                      setTxPage(1);
                    }}
                    sx={{ color: 'text.secondary', '&:hover': { color: '#ff9800' } }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {txData?.meta?.total != null && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {txData.meta.total.toLocaleString()} transaction
                  {txData.meta.total !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          )}

          {/* DataGrids */}
          {tab === 'inventory' && (
            <DataGrid
              rows={data?.data || []}
              columns={invColumns}
              loading={isLoading || isFetching}
              paginationMode="server"
              rowCount={data?.meta?.total || 0}
              pageSizeOptions={[15, 25, 50]}
              paginationModel={{ page: page - 1, pageSize }}
              onPaginationModelChange={(m) => {
                setPage(m.page + 1);
                setPageSize(m.pageSize);
              }}
              autoHeight
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
              sx={GRID_SX}
            />
          )}

          {tab === 'transactions' && (
            <DataGrid
              rows={txData?.data || []}
              columns={txColumns}
              loading={txLoading || txFetching}
              paginationMode="server"
              rowCount={txData?.meta?.total || 0}
              pageSizeOptions={[15, 25, 50]}
              paginationModel={{ page: txPage - 1, pageSize: txPageSize }}
              onPaginationModelChange={(m) => {
                setTxPage(m.page + 1);
                setTxPageSize(m.pageSize);
              }}
              autoHeight
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
              sx={GRID_SX}
            />
          )}
        </Box>
      </Paper>

      {/* ── Dialogs ── */}
      <MedicationFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setSelectedMedication(null);
        }}
        medication={selectedMedication}
      />

      {/* Procurement picker (for usage transactions) */}
      <Dialog
        open={procurementPickerOpen}
        onClose={() => setProcurementPickerOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <Box
          sx={{
            px: 2.5,
            pt: 2,
            pb: 1.5,
            background: 'linear-gradient(135deg, rgba(38,198,218,0.1) 0%, transparent 100%)',
            borderBottom: '1px solid rgba(38,198,218,0.15)',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Select Bird Batch
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Which procurement is this medication being used for?
          </Typography>
        </Box>
        <DialogContent sx={{ pt: '16px !important' }}>
          <FormControl fullWidth>
            <InputLabel>Procurement (Batch)</InputLabel>
            <Select
              value={selectedProcurementId}
              label="Procurement (Batch)"
              onChange={(e) => setSelectedProcurementId(e.target.value as number)}
            >
              {procurements.map((p: any) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.identifier} — {p.date_procured}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcurementPickerOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={selectedProcurementId === ''}
            onClick={() => {
              setProcurementPickerOpen(false);
              setTransactionDialogOpen(true);
            }}
            sx={{ background: 'linear-gradient(135deg, #26c6da 0%, #00838f 100%)', color: '#000' }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <MedicationTransactionDialog
        open={transactionDialogOpen}
        onClose={() => {
          setTransactionDialogOpen(false);
          setSelectedMedication(null);
          setSelectedProcurementId('');
        }}
        medication={selectedMedication}
        transactionType={transactionType}
        procurementId={selectedProcurementId !== '' ? selectedProcurementId : undefined}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Medication"
        message={`Are you sure you want to delete ${selectedMedication?.name}? This action cannot be undone.`}
        onConfirm={() => selectedMedication && deleteMedMutation.mutate(selectedMedication.id)}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedMedication(null);
        }}
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleteMedMutation.isPending}
      />

      {/* Delete transaction confirmation */}
      <Dialog
        open={!!txDeleteTarget}
        onClose={() => setTxDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <Box
          sx={{
            px: 2.5,
            pt: 2,
            pb: 1.5,
            background: 'linear-gradient(135deg, rgba(244,67,54,0.1) 0%, transparent 100%)',
            borderBottom: '1px solid rgba(244,67,54,0.2)',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Delete Transaction
          </Typography>
        </Box>
        <DialogContent>
          <DialogContentText>
            Delete this <strong>{txDeleteTarget?.transaction_type}</strong> transaction for{' '}
            <strong>{txDeleteTarget?.medication?.name}</strong> ({txDeleteTarget?.quantity}{' '}
            {txDeleteTarget?.medication?.unit} on{' '}
            {txDeleteTarget?.transaction_date
              ? format(new Date(txDeleteTarget.transaction_date), 'dd MMM yyyy')
              : ''}
            )? Stock will be reversed and any linked expenditure removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTxDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteTxMutation.isPending}
            onClick={() => txDeleteTarget && deleteTxMutation.mutate(txDeleteTarget.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Medications;
